import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { MapPinOff, CheckCircle2, Loader2 } from "lucide-react";
import { PageHeader } from "../../../components/ui/PageHeader";
import { EmptyState } from "../../../components/ui/EmptyState";
import { Badge } from "../../../components/ui/Badge";
import { CopyButton } from "../../../components/ui/CopyButton";
import { Button } from "../../../components/ui/Button";
import { Modal } from "../../../components/ui/Modal";
import AddressPicker from "../../../components/addressPicker/AddressPicker";
import {
  getPendingAddresses,
  resolvePendingAddress,
} from "../../../api/packages";
import { showToast } from "../../../utils/showToast";
import { geocodeDefaultsFor } from "../../../utils/geocode";
import { useCountry } from "../../../context/CountryContext";

const PAGE_SIZE = 20;

/**
 * Por qué quedó pendiente cada dirección, en palabras del operador y no del
 * geocodificador. La distinción importa para saber qué hacer con cada fila:
 * `approximate` suele resolverse confirmando el pin, `not_found` casi siempre
 * necesita que alguien lea la dirección y la reescriba.
 */
const REASON = {
  approximate: {
    tone: "warning",
    label: "Número aproximado",
    help: "Se encontró la calle, pero no esa altura.",
  },
  unrelated: {
    tone: "error",
    label: "Calle distinta",
    help: "Lo más parecido que se encontró es otra calle.",
  },
  not_found: {
    tone: "neutral",
    label: "Sin resultados",
    help: "No se encontró ninguna dirección parecida.",
  },
};

const addressText = (pkg) => {
  const a = pkg?.shipping_address || {};
  return (
    [a.address_line, a.municipality?.name].filter(Boolean).join(", ") ||
    a.street_name ||
    ""
  );
};

/**
 * Bandeja de direcciones por resolver.
 *
 * Estos paquetes NO tienen coordenada, así que el optimizador los deja fuera de
 * la ruta: mientras estén acá, viajan sin parada asignada en la secuencia del
 * repartidor. Por eso la pantalla se ordena por antigüedad y no por número de
 * pedido — lo que primero hay que resolver es lo que está por salir a reparto.
 */
export default function PendingAddresses() {
  const { t } = useTranslation();
  // `countryCode`, no `country`: el contexto expone
  // { loading, resolved, countryId, countryCode, countryName, ... } y no hay
  // ninguna clave `country`. Leer la equivocada dejaba el sesgo geográfico en
  // Chile SIEMPRE, con lo que la bandeja era inservible en Argentina.
  const { countryCode } = useCountry() || {};

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const geoDefaults = useMemo(
    () => geocodeDefaultsFor(countryCode),
    [countryCode],
  );

  const load = useCallback(
    async (targetPage) => {
      setLoading(true);
      try {
        const { data } = await getPendingAddresses({
          page: targetPage,
          limit: PAGE_SIZE,
        });
        setRows(data?.docs || []);
        setTotal(data?.totalDocs || 0);
        setTotalPages(data?.totalPages || 0);
        setPage(data?.page || targetPage);
      } catch {
        showToast(t("No se pudo cargar la lista de direcciones"), "error");
        setRows([]);
      } finally {
        setLoading(false);
      }
    },
    [t],
  );

  useEffect(() => {
    load(1);
  }, [load]);

  const onConfirm = async ({ lat, lng, label }) => {
    if (!editing) return;
    setSaving(true);
    try {
      await resolvePendingAddress(editing._id, { lat, lng, label });
      showToast(t("Dirección resuelta"), "success");
      setEditing(null);
      // Se recarga la página actual en vez de sacar la fila a mano: si era la
      // última de la página, quedarse en una página vacía confunde más que el
      // parpadeo de recargar.
      load(page);
    } catch (err) {
      showToast(
        err?.response?.data?.message || t("No se pudo guardar la ubicación"),
        "error",
      );
    } finally {
      setSaving(false);
    }
  };

  // `w-full min-w-0` en la raíz: el padre (`.content-main-container`) es un flex de
  // columna con `align-items: center`, así que sin ancho propio la vista se dimensiona
  // a su min-content —que acá marca la tabla de 720px— y en el teléfono la página entera
  // quedaba del doble de ancho, con scroll horizontal y el título cortado.
  return (
    <div className="flex w-full min-w-0 flex-col gap-4 p-4">
      <PageHeader
        icon={<MapPinOff aria-hidden="true" />}
        title={t("Direcciones por resolver")}
        subtitle={
          total > 0
            ? t(
                "{{count}} pedidos sin ubicación confirmada. Hasta resolverlos no entran a la ruta.",
                { count: total },
              )
            : t("No hay direcciones pendientes.")
        }
      />

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-16 text-fg-muted">
          <Loader2 size={18} className="animate-spin" aria-hidden="true" />
          {t("Cargando...")}
        </div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={<CheckCircle2 aria-hidden="true" />}
          title={t("Todo en orden")}
          description={t(
            "Todos los pedidos tienen su ubicación confirmada y pueden entrar a una ruta.",
          )}
        />
      ) : (
        <>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full min-w-[720px] text-sm">
              <thead className="bg-bg-subtle text-left text-xs uppercase text-fg-muted">
                <tr>
                  <th className="px-3 py-2 font-medium">{t("Pedido")}</th>
                  <th className="px-3 py-2 font-medium">{t("Destinatario")}</th>
                  <th className="px-3 py-2 font-medium">{t("Dirección declarada")}</th>
                  <th className="px-3 py-2 font-medium">{t("Motivo")}</th>
                  <th className="px-3 py-2 font-medium sr-only">{t("Acción")}</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((pkg) => {
                  const reason =
                    REASON[pkg?.geocoding?.matchQuality] || REASON.not_found;
                  return (
                    <tr key={pkg._id} className="border-t border-border align-top">
                      <td className="px-3 py-2 tabular-nums">
                        <span className="inline-flex items-center gap-0.5">
                          {pkg.followingNumber ?? "—"}
                          <CopyButton
                            value={pkg.followingNumber}
                            label={t("Copiar número de seguimiento")}
                            copiedMessage={t("Número de seguimiento copiado")}
                          />
                        </span>
                        {pkg.order_id && (
                          <span className="flex items-center gap-0.5 text-xs text-fg-muted">
                            {pkg.order_id}
                            <CopyButton
                              value={pkg.order_id}
                              label={t("Copiar número de venta")}
                              copiedMessage={t("Número de venta copiado")}
                            />
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {[pkg.receiver_name, pkg.receiver_lastName]
                          .filter(Boolean)
                          .join(" ") || "—"}
                      </td>
                      <td className="px-3 py-2">
                        <span className="block">{addressText(pkg)}</span>
                        {pkg?.shipping_address?.comment && (
                          <span className="block text-xs text-fg-muted">
                            {pkg.shipping_address.comment}
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        <Badge tone={reason.tone}>{t(reason.label)}</Badge>
                        <span className="mt-1 block text-xs text-fg-muted">
                          {t(reason.help)}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Button size="sm" onClick={() => setEditing(pkg)}>
                          {t("Resolver")}
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-fg-muted">
                {t("Página {{page}} de {{totalPages}}", { page, totalPages })}
              </span>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => load(page - 1)}
                >
                  {t("Anterior")}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => load(page + 1)}
                >
                  {t("Siguiente")}
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      <Modal
        open={!!editing}
        onClose={() => (saving ? null : setEditing(null))}
        title={t("Resolver dirección")}
        description={editing ? addressText(editing) : undefined}
        size="lg"
      >
        {editing && (
          <AddressPicker
            // El id de instancia evita que dos pickers compartan los ids del
            // combobox si en el futuro se resuelve más de uno a la vez.
            instanceId={editing._id}
            initialQuery={addressText(editing)}
            initialPoint={
              // El candidato que el geocoder descartó. Sembrar el pin ahí ahorra
              // el paso de buscar de cero en el caso frecuente —la calle estaba
              // bien y sólo fallaba la altura—, y no es la coordenada del
              // paquete: el ruteo no lo lee y no se persiste hasta confirmar.
              editing?.geocoding?.suggestion?.lat != null
                ? {
                    lat: editing.geocoding.suggestion.lat,
                    lng: editing.geocoding.suggestion.lng,
                  }
                : null
            }
            countryCode={geoDefaults.countryCode}
            focusCenter={geoDefaults.center}
            busy={saving}
            confirmLabel={t("Confirmar ubicación")}
            onConfirm={onConfirm}
            onCancel={() => setEditing(null)}
          />
        )}
      </Modal>
    </div>
  );
}
